import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export function PaperworkCampaignCard() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [conflictsOnly, setConflictsOnly] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/admin/paperwork-readiness").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const rows = useMemo(() => data?.rows || [], [data]);
  const attentionCount = rows.filter((r) => r.date_conflict || r.questionnaire_date_conflict || !r.effective_date).length;
  const shown = useMemo(() => rows.filter((r) => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const hay = (r.name || "").toLowerCase();
    const matches = words.every((w) => hay.includes(w));
    const flagged = r.date_conflict || r.questionnaire_date_conflict || !r.effective_date;
    const statusAllowed = includeInactive || r.status === "Active";
    return matches && statusAllowed && (!conflictsOnly || flagged);
  }), [rows, q, conflictsOnly, includeInactive]);

  if (!data) return null;

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };
  const readBlobError = async (blob) => {
    try {
      const parsed = JSON.parse(await blob.text());
      return parsed.detail || "Download failed";
    } catch {
      return "Download failed";
    }
  };
  const fetchBlob = async (path) => {
    const res = await api.get(path, { responseType: "blob" });
    if ((res.data.type || "").includes("json")) throw new Error(await readBlobError(res.data));
    return res.data;
  };
  const saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  const openAgreement = async (row) => {
    setBusy(row.client_id);
    try {
      const blob = await fetchBlob(`/clients/${row.client_id}/agreement-original.pdf`);
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) saveBlob(blob, `agreement-${row.name || row.client_id}.pdf`);
      else setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.message || formatApiError(e));
    } finally {
      setBusy("");
    }
  };
  const openRoleForm = async (row) => {
    setBusy(`role-${row.client_id}`);
    try {
      const blob = await fetchBlob(`/clients/${row.client_id}/forms/dcw-role-confirmation.pdf`);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.message || formatApiError(e));
    } finally {
      setBusy("");
    }
  };
  const downloadZip = async () => {
    setBusy("zip");
    try {
      const blob = await fetchBlob("/admin/paperwork/print-all");
      saveBlob(blob, `agreements-served-since-${data.cutoff}.zip`);
      toast.success(`Downloaded ${rows.length} agreements`);
    } catch (e) {
      toast.error(e.message || formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const downloadRoleZip = async () => {
    setBusy("rolezip");
    try {
      const blob = await fetchBlob("/admin/paperwork/dcw-role-confirmations.zip");
      saveBlob(blob, "dcw-role-confirmations.zip");
      toast.success(`Downloaded ${rows.length} role confirmation forms`);
    } catch (e) {
      toast.error(e.message || formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="mb-8 rounded-xl border border-[#4A235A]/25 bg-white shadow-sm" data-testid="paperwork-campaign-card">
      <div className="border-b border-stone-100 px-6 py-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-3xl">
            <p className="font-heading text-lg font-semibold text-stone-900">Agreement recovery review</p>
            <p className="mt-1 text-xs leading-relaxed text-stone-500">
              Every real client with service on or after {fmtDate(data.cutoff)} — active or inactive — sorted by the first service date in that period. Effective date uses the earliest imported service record; roster start is shown beside it for review. Questionnaire schedule text appears in Notes when available; schedule and caregiver assignment fields stay blank.
            </p>
            <p className="mt-2 text-[11px] font-semibold text-[#B7791F]" data-testid="agreement-review-note">
              Review first: {attentionCount} row{attentionCount === 1 ? "" : "s"} need date attention · {data.summary?.reviewed || 0} of {rows.length} reviewed · {data.summary?.questionnaires_matched || 0} intake questionnaire{(data.summary?.questionnaires_matched || 0) === 1 ? "" : "s"} matched · {data.summary?.restored_packets || 0} first-version packet file{(data.summary?.restored_packets || 0) === 1 ? "" : "s"} marked. A restored packet file is only a source artifact — it is not being treated as proof of signature.
            </p>
            <p className="mt-1 text-[11px] text-stone-500">Click a client name to review the record. When finished, check the review box to enable PDF printing.</p>
          </div>
          <div className="flex flex-col items-stretch gap-2">
            <label className="flex items-start gap-2 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-700">
              <input type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)}
                className="mt-0.5" data-testid="agreement-matrix-reviewed-checkbox" />
              <span>I reviewed the client list and date conflicts. Enable PDF printing.</span>
            </label>
            <button onClick={downloadZip} disabled={!reviewed || busy === "zip"}
              className="rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground transition-colors hover:bg-stone-900 disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="resign-zip-button">
              {busy === "zip" ? "Preparing ZIP…" : `Download ${rows.length} reviewed agreements (ZIP)`}
            </button>
            <button onClick={downloadRoleZip} disabled={!reviewed || busy === "rolezip"}
              className="rounded-full border border-stone-300 px-4 py-2 text-xs font-semibold text-stone-700 transition-colors hover:bg-stone-100 disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="dcw-role-zip-button">
              {busy === "rolezip" ? "Preparing ZIP…" : `Download ${rows.length} role confirmations (ZIP)`}
            </button>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search this review list only…"
            className="h-8 w-56 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid="agreement-matrix-search" />
          <label className="flex items-center gap-2 text-xs text-stone-600">
            <input type="checkbox" checked={conflictsOnly} onChange={(e) => setConflictsOnly(e.target.checked)} data-testid="agreement-conflicts-only" />
            Show date-review rows only
          </label>
          <label className="flex items-center gap-2 text-xs text-stone-600">
            <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} data-testid="agreement-include-inactive" />
            Include inactive clients ({rows.filter((r) => r.status !== "Active").length})
          </label>
          <span className="text-[11px] text-stone-400" data-testid="agreement-matrix-count">Showing {shown.length} of {rows.length}</span>
        </div>
      </div>

      <div className="max-h-[32rem] overflow-y-auto px-2 py-2" data-testid="resign-list">
        <div className="hidden grid-cols-[2.5rem_minmax(13rem,1.4fr)_repeat(3,minmax(8rem,0.7fr))_minmax(12rem,1fr)_7rem] gap-3 border-b border-stone-100 px-4 pb-2 text-[10px] font-bold uppercase tracking-wide text-stone-400 lg:grid">
          <span>#</span><span>Client</span><span>First service since cutoff</span><span>Earliest service</span><span>Roster start</span><span>Review</span><span></span>
        </div>
        {shown.map((r, idx) => (
          <div key={r.client_id} className="grid grid-cols-1 gap-2 rounded-md px-4 py-3 text-xs hover:bg-stone-50 lg:grid-cols-[2.5rem_minmax(13rem,1.4fr)_repeat(3,minmax(8rem,0.7fr))_minmax(12rem,1fr)_7rem] lg:items-center lg:gap-3"
            data-testid={`resign-row-${r.client_id}`}>
            <span className="text-stone-400">{String(idx + 1).padStart(2, "0")}</span>
            <span className="min-w-0">
              <Link to={`/clients/${r.client_id}`} className="block truncate text-sm font-medium text-stone-800 hover:text-primary hover:underline"
                data-testid={`agreement-review-client-${r.client_id}`}>{r.name}</Link>
              <Link to={`/clients/${r.client_id}/service-agreement`} className="mt-0.5 inline-block text-[11px] font-semibold text-primary hover:underline"
                data-testid={`agreement-edit-form-${r.client_id}`}>Edit agreement form</Link>
              <button onClick={() => openRoleForm(r)} disabled={!reviewed || busy === `role-${r.client_id}`}
                className="mt-0.5 block text-left text-[11px] font-semibold text-primary hover:underline disabled:text-stone-300"
                data-testid={`dcw-role-form-${r.client_id}`}>
                {busy === `role-${r.client_id}` ? "Opening role form…" : "Role confirmation PDF"}
              </button>
              <span className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold ${r.status === "Active" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-stone-200 bg-stone-100 text-stone-500"}`}>
                {r.status || "Unknown"}
              </span>
            </span>
            <span data-testid={`agreement-first-since-${r.client_id}`}>{fmtDate(r.first_service_since_cutoff)}</span>
            <span data-testid={`agreement-first-service-${r.client_id}`}>{fmtDate(r.first_service)}</span>
            <span data-testid={`agreement-roster-start-${r.client_id}`}>{fmtDate(r.start)}</span>
            <span className="flex flex-wrap gap-1" data-testid={`agreement-flags-${r.client_id}`}>
              {r.date_conflict && <span className="rounded-full border border-[#B7791F]/40 bg-[#B7791F]/10 px-2 py-0.5 text-[10px] font-bold text-[#8a5d14]">check date</span>}
              {!r.effective_date && <span className="rounded-full border border-[#C0392B]/40 bg-[#C0392B]/10 px-2 py-0.5 text-[10px] font-bold text-[#C0392B]">missing date</span>}
              {r.reviewed_by && <span title={`Reviewed on ${r.reviewed_at || "—"}`} className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">reviewed by {r.reviewed_by}</span>}
              {r.questionnaire_on_file && <span title={`Questionnaire effective ${r.questionnaire_effective_date || "—"} · proposed start ${r.questionnaire_proposed_start_date || "—"}`} className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${r.questionnaire_date_conflict ? "border-[#B7791F]/40 bg-[#B7791F]/10 text-[#8a5d14]" : "border-primary/20 bg-primary/5 text-primary"}`}>intake questionnaire{r.questionnaire_date_conflict ? " · date differs" : ""}</span>}
              {r.restored_packet && <span title="Historical first-version packet file restored; signature not verified" className="rounded-full border border-stone-200 bg-stone-50 px-2 py-0.5 text-[10px] font-semibold text-stone-500">first-version packet · signature not verified</span>}
              {!r.has_careplan && <span className="rounded-full border border-stone-200 bg-stone-50 px-2 py-0.5 text-[10px] font-semibold text-stone-500">no care plan</span>}
            </span>
            <button onClick={() => openAgreement(r)} disabled={!reviewed || busy === r.client_id}
              className="justify-self-start text-xs font-semibold text-primary hover:underline disabled:cursor-not-allowed disabled:text-stone-300 lg:justify-self-end"
              data-testid={`resign-print-${r.client_id}`}>
              {busy === r.client_id ? "Opening…" : "Open current agreement"}
            </button>
          </div>
        ))}
        {shown.length === 0 && <p className="px-4 py-8 text-center text-xs text-stone-400" data-testid="agreement-matrix-empty">No clients match this review filter.</p>}
      </div>
    </div>
  );
}
